import type { Question } from "../client/src/data/simulado";

export const QUESTIONS_PER_AREA_PER_ATTEMPT = 25;
export const ATTEMPT_AREA_COUNT = 4;
export const ATTEMPT_QUESTION_COUNT = QUESTIONS_PER_AREA_PER_ATTEMPT * ATTEMPT_AREA_COUNT;
export const ATTEMPT_AREA_ORDER = [
  "Linguagens, Códigos e suas Tecnologias",
  "Ciências Humanas e suas Tecnologias",
  "Ciências da Natureza e suas Tecnologias",
  "Matemática e suas Tecnologias",
] as const;

const LETTERS = ["A", "B", "C", "D"] as const;
type Letter = (typeof LETTERS)[number];

function normalizeFourOptionQuestion(question: Question): Question {
  if (question.visual || !question.gabaritoOriginal || !question.alternativas) return question;
  const entries = Object.entries(question.alternativas)
    .filter(([letter, value]) => /^[A-E]$/.test(letter) && typeof value === "string");
  if (entries.length !== 4) return question;
  const correctIndex = entries.findIndex(([letter]) => letter === question.gabaritoOriginal);
  if (correctIndex < 0) return question;
  const alternatives = Object.fromEntries(
    entries.map(([, value], index) => [String.fromCharCode(65 + index), value]),
  ) as Question["alternativas"];
  return {
    ...question,
    alternativas: alternatives,
    correta: String.fromCharCode(65 + correctIndex),
  };
}

type SeedSource = string | number;

function hashSeed(...parts: SeedSource[]) {
  let hash = 2166136261;
  for (const part of parts) {
    const text = String(part);
    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
  }
  return hash >>> 0;
}

function nextRandom(state: { value: number }) {
  state.value += 0x6D2B79F5;
  let value = state.value;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
}

export function seededShuffle<T>(items: readonly T[], ...seed: SeedSource[]) {
  const result = [...items];
  const state = { value: hashSeed(...seed) };
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(nextRandom(state) * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
}

function shuffleAlternatives(question: Question, studentKey: string, attemptNumber: number) {
  const shuffledLetters = seededShuffle(LETTERS, studentKey, attemptNumber, question.numero, "alternatives");
  const alternatives = {} as Question["alternativas"];
  let correta: Letter = "A";
  shuffledLetters.forEach((sourceLetter, index) => {
    const targetLetter = LETTERS[index];
    alternatives[targetLetter] = question.alternativas[sourceLetter];
    if (sourceLetter === question.correta) correta = targetLetter;
  });
  return { ...question, alternativas: alternatives, correta };
}

export function buildAttemptQuestions(
  bank: readonly Question[],
  studentKey: string,
  attemptNumber: number,
  excludedQuestionIds: readonly string[] = [],
) {
  if (!Number.isInteger(attemptNumber) || attemptNumber < 1) throw new Error("A tentativa deve ser um número inteiro positivo.");
  const bankAreas = new Set(bank.map((question) => question.area));
  if (bankAreas.size !== ATTEMPT_AREA_COUNT || ATTEMPT_AREA_ORDER.some((area) => !bankAreas.has(area))) {
    throw new Error(`O banco deve conter as ${ATTEMPT_AREA_COUNT} áreas do simulado.`);
  }

  const chosenByArea = ATTEMPT_AREA_ORDER.map((area) => {
    const excluded = new Set(excludedQuestionIds);
    const availablePool = bank.filter((question) => question.area === area && !excluded.has(question.id));
    const pool = availablePool.length >= QUESTIONS_PER_AREA_PER_ATTEMPT
      ? availablePool
      : bank.filter((question) => question.area === area);
    const shuffledPool = seededShuffle(pool, studentKey, area, "pool");
    const start = excludedQuestionIds.length ? 0 : (attemptNumber - 1) * QUESTIONS_PER_AREA_PER_ATTEMPT;
    const end = start + QUESTIONS_PER_AREA_PER_ATTEMPT;
    if (shuffledPool.length < end) throw new Error(`A área ${area} não tem questões suficientes para a tentativa ${attemptNumber}.`);
    return seededShuffle(shuffledPool.slice(start, end), studentKey, attemptNumber, area, "block-order");
  });

  // `numero` is the position shown in this attempt. `id` remains the stable bank
  // identifier used to trace the original item independently of that position.
  return chosenByArea
    .flatMap((items) => items)
    .map(normalizeFourOptionQuestion)
    .map((question) => shuffleAlternatives(question, studentKey, attemptNumber))
    .map((question, index) => ({ ...question, numero: index + 1 }));
}
